import { apiFetch } from './client';

export type FeatureSource = 'core' | 'customer' | 'reseller' | 'owner_default';

/**
 * Slim catalog row as returned by GET /feature-flags/catalog.
 *
 * Note: `owner_monthly_price_cents` is intentionally NOT present. Pricing is
 * now per-reseller — the super-admin sets it from the per-reseller settings
 * page. The catalog itself is just: key/name/category/default-on/core.
 */
export interface FeatureFlagCatalogRow {
  key: string;
  display_name: string;
  category: string;
  description: string | null;
  default_enabled: boolean;
  core: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface EffectiveFeatureRow {
  key: string;
  display_name: string;
  category: string;
  description: string | null;
  core: boolean;
  enabled: boolean;
  /** Resolved owner→reseller price (per-reseller override ?? catalog fallback ?? 0).
   *  ABSENT for customer-tier requesters — the backend strips price fields
   *  for office_manager and below (2026-08-06). */
  owner_monthly_price_cents?: number;
  /** What the reseller charges their customers. NULL = pass-through.
   *  ABSENT for customer-tier requesters (see above). */
  reseller_monthly_price_cents?: number | null;
  source: FeatureSource;
  sort_order: number;
}

export interface EffectiveResponse {
  rows: EffectiveFeatureRow[];
  scope: { resellerId: string | null; customerId: string | null };
}

/**
 * Per-reseller editor row. Used by the super-admin drill-in at
 * /resellers/:id/settings/features and by the reseller's own self-view.
 */
export interface ResellerEditorRow {
  key: string;
  display_name: string;
  category: string;
  description: string | null;
  core: boolean;
  enabled: boolean;
  /** What the OWNER charges THIS reseller (per-reseller override ?? catalog fallback ?? 0). */
  owner_monthly_price_cents: number;
  /** Only present for super-admin: whether the owner price comes from a per-reseller override. */
  owner_price_is_override?: boolean;
  /** What the reseller charges their customers (NULL = not set). */
  reseller_monthly_price_cents: number | null;
  source: FeatureSource;
  sort_order: number;
}

export interface ResellerEditorResponse {
  rows: ResellerEditorRow[];
  reseller_id: string;
}

export type CatalogPatch = Partial<{
  display_name: string;
  category: string;
  description: string | null;
  default_enabled: boolean;
  core: boolean;
  sort_order: number;
}>;

export interface CatalogCreate {
  key: string;
  display_name: string;
  category?: string;
  description?: string | null;
  default_enabled?: boolean;
  core?: boolean;
  sort_order?: number;
}

export const featureFlags = {
  catalog: () => apiFetch<{ rows: FeatureFlagCatalogRow[] }>(`/api/feature-flags/catalog`),
  createCatalog: (body: CatalogCreate) =>
    apiFetch<FeatureFlagCatalogRow>(`/api/feature-flags/catalog`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  updateCatalog: (key: string, body: CatalogPatch) =>
    apiFetch<FeatureFlagCatalogRow>(`/api/feature-flags/catalog/${encodeURIComponent(key)}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),

  effective: (p: { reseller_id?: string; customer_id?: string } = {}) => {
    const q = new URLSearchParams();
    if (p.reseller_id) q.set('reseller_id', p.reseller_id);
    if (p.customer_id) q.set('customer_id', p.customer_id);
    const qs = q.toString();
    return apiFetch<EffectiveResponse>(`/api/feature-flags/effective${qs ? `?${qs}` : ''}`);
  },

  /** GET /feature-flags/by-reseller/:id — editor view (enabled + both prices). */
  byReseller: (resellerId: string) =>
    apiFetch<ResellerEditorResponse>(
      `/api/feature-flags/by-reseller/${encodeURIComponent(resellerId)}`,
    ),

  /** PUT /feature-flags/by-reseller/:id/:key — super-admin sets all three. */
  setByReseller: (
    resellerId: string,
    key: string,
    body: {
      enabled: boolean;
      owner_monthly_price_cents?: number | null;
      reseller_monthly_price_cents?: number | null;
    },
  ) =>
    apiFetch<{
      reseller_id: string;
      feature_key: string;
      enabled: boolean;
      owner_monthly_price_cents: number | null;
      reseller_monthly_price_cents: number | null;
    }>(
      `/api/feature-flags/by-reseller/${encodeURIComponent(resellerId)}/${encodeURIComponent(key)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  // Legacy shortcut path — still used by the FeatureGate upsell link. Kept
  // for backward-compat; new code should use setByReseller.
  setReseller: (
    key: string,
    body: {
      enabled: boolean;
      owner_monthly_price_cents?: number | null;
      reseller_monthly_price_cents?: number | null;
      reseller_id?: string;
    },
  ) =>
    apiFetch<{
      reseller_id: string;
      feature_key: string;
      enabled: boolean;
      owner_monthly_price_cents: number | null;
      reseller_monthly_price_cents: number | null;
    }>(
      `/api/feature-flags/reseller/${encodeURIComponent(key)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  clearReseller: (key: string, resellerId?: string) => {
    const q = resellerId ? `?reseller_id=${encodeURIComponent(resellerId)}` : '';
    return apiFetch<void>(`/api/feature-flags/reseller/${encodeURIComponent(key)}${q}`, { method: 'DELETE' });
  },

  setCustomer: (key: string, body: { enabled: boolean; customer_id?: string }) =>
    apiFetch<{ customer_id: string; feature_key: string; enabled: boolean }>(
      `/api/feature-flags/customer/${encodeURIComponent(key)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  clearCustomer: (key: string, customerId?: string) => {
    const q = customerId ? `?customer_id=${encodeURIComponent(customerId)}` : '';
    return apiFetch<void>(`/api/feature-flags/customer/${encodeURIComponent(key)}${q}`, { method: 'DELETE' });
  },
};
